import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    Alert,
    Button,
    ButtonGroup,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
} from "reactstrap";
import api from "../services/api";
import Header from "../components/public/Header";
import Footer from "../components/public/Footer";

const TOTAL_HOLES = 9;
const DEFAULT_PAR = 4;
const LONGEST_PUTT = ['LP','LP','LP','LP','LP','LP','LP','LP','LP'];
const DEFAULT_SAMPLE_ID = "1";

const COURSE_SAMPLES = [
    {
        id: "1",
        weekCourseLabel: "Week 13 - Nine Dragons B&C (Front)",
        holeNumbers: [1, 2, 3, 4, 5, 6, 7, 8, 9],
        holePars: [4, 4, 5, 3, 4, 4, 3, 5, 4],
        holePrizes: LONGEST_PUTT,
    },
    {
        id: "2",
        weekCourseLabel: "Week 13 - Olympic Club (Back Nine)",
        holeNumbers: [10, 11, 12, 13, 14, 15, 16, 17, 18],
        holePars: [4, 4, 4, 3, 4, 3, 5, 5, 4],
        holePrizes: ['','','','','','CTP','','',''],
    },
];

const getDefaultCourseSample = () =>
    COURSE_SAMPLES.find((sample) => sample.id === DEFAULT_SAMPLE_ID) || COURSE_SAMPLES[0];

const getCourseSampleFromQuery = (searchValue) => {
    const queryParams = new URLSearchParams(searchValue);
    const sampleId = queryParams.get("sample") || DEFAULT_SAMPLE_ID;

    const selectedSample = COURSE_SAMPLES.find((sample) => sample.id === sampleId);

    if (selectedSample) {
        return selectedSample;
    }

    return getDefaultCourseSample();
};

const buildHolesFromSample = (sample) =>
    Array.from({ length: TOTAL_HOLES }, (_, index) => ({
        holeNumber: sample.holeNumbers?.[index] ?? (index + 1),
        holePar: sample.holePars[index] ?? DEFAULT_PAR,
        holePrize: sample.holePrizes?.[index] ?? "",
    }));

const buildDefaultHoles = () =>
    buildHolesFromSample(getDefaultCourseSample());

const initialPlayers = [
    { id: 1, name: "Player 1", scores: Array(TOTAL_HOLES).fill(null) },
    { id: 2, name: "Player 2", scores: Array(TOTAL_HOLES).fill(null) },
];

const getScoreVisualStyle = (score, par) => {
    const delta = score - par;

    if (delta === -1) {
        return {
            border: "2px solid",
            borderRadius: "9999px",
        };
    }

    if (delta <= -2) {
        return {
            border: "2px solid",
            borderRadius: "9999px",
            boxShadow: "0 0 0 3px var(--bs-body-bg), 0 0 0 5px currentColor",
        };
    }

    if (delta === 1) {
        return {
            border: "2px solid",
            borderRadius: "0",
        };
    }

    if (delta >= 2) {
        return {
            border: "2px solid",
            borderRadius: "0",
            boxShadow: "0 0 0 3px var(--bs-body-bg), 0 0 0 5px currentColor",
        };
    }

    return {};
};

const getRelativeToParMeta = (totalScore, totalPar) => {
    const delta = totalScore - totalPar;

    if (delta > 0) {
        return { text: `+${delta}`, className: "text-success" };
    }

    if (delta < 0) {
        return { text: `${delta}`, className: "text-danger" };
    }

    return { text: "E", className: "text-muted" };
};

const getPrizeHeader = (prizeCode) => {
    if (prizeCode === "CTP") {
        return "Closest to Pin";
    }

    if (prizeCode === "LP") {
        return "Longest Putt";
    }

    return "Hole Prize";
};

const LeagueTeamScore = () => {
    const location = useLocation();
    const selectedCourseSample = useMemo(
        () => getCourseSampleFromQuery(location.search),
        [location.search]
    );

    const [organization, setOrganization] = useState(null);
    const [loadingOrg, setLoadingOrg] = useState(true);
    const [holes, setHoles] = useState(buildDefaultHoles());
    const [players, setPlayers] = useState(initialPlayers);
    const [playerTotalScores, setPlayerTotalScores] = useState([]);
    const [scoreEntryMode, setScoreEntryMode] = useState("player");
    const [weekCourseLabel, setWeekCourseLabel] = useState("Week 13 - Nine Dragons B&C (Front)");
    const [activePlayerIndex, setActivePlayerIndex] = useState(0);
    const [activeHoleIndex, setActiveHoleIndex] = useState(0);
    const [showSubmittedScores, setShowSubmittedScores] = useState(false);
    const [scoresConfirmed, setScoresConfirmed] = useState(false);
    const [scorePhotoName, setScorePhotoName] = useState("");
    const [showPrizeModal, setShowPrizeModal] = useState(false);
    const [prizeFeet, setPrizeFeet] = useState("");
    const [prizeInches, setPrizeInches] = useState("");
    const [prizeEntries, setPrizeEntries] = useState({});
    const [activePrizeContext, setActivePrizeContext] = useState({
        playerIndex: null,
        holeIndex: null,
        prizeCode: "",
    });

    useEffect(() => {
        document.title = "League Team Score - Reserve2Day";
    }, []);

    useEffect(() => {
        const fetchOrganization = async () => {
            try {
                const response = await api.get("/get-org-details/");
                setOrganization(response.data);
            } catch (error) {
                setOrganization(null);
            } finally {
                setLoadingOrg(false);
            }
        };

        fetchOrganization();
    }, []);

    useEffect(() => {
        const fetchHoleInformation = async () => {
            const holeData = await Promise.resolve(buildHolesFromSample(selectedCourseSample));

            setHoles(holeData);
            setPlayers((currentPlayers) =>
                currentPlayers.map((player) => ({
                    ...player,
                    scores: holeData.map((hole, scoreIndex) => player.scores[scoreIndex] ?? hole.holePar),
                }))
            );
            setPrizeEntries({});
        };

        fetchHoleInformation();
    }, [selectedCourseSample]);

    useEffect(() => {
        const fetchWeekCourseLabel = async () => {
            const label = await Promise.resolve(selectedCourseSample.weekCourseLabel);
            setWeekCourseLabel(label);
        };

        fetchWeekCourseLabel();
    }, [selectedCourseSample]);

    const activePlayer = useMemo(() => players[activePlayerIndex], [players, activePlayerIndex]);
    const activeHole = holes[activeHoleIndex] || { holeNumber: "-", holePar: 0 };
    const totalPar = holes.reduce((sum, hole) => sum + (hole.holePar ?? 0), 0);
    const prizeHoles = useMemo(
        () => holes.map((hole, holeIndex) => ({ ...hole, holeIndex })).filter((hole) => Boolean(hole.holePrize)),
        [holes]
    );

    useEffect(() => {
        setPlayerTotalScores((currentTotals) =>
            players.map((player, playerIndex) => {
                if (typeof currentTotals[playerIndex] === "number") {
                    return currentTotals[playerIndex];
                }

                const computedTotal = player.scores.reduce(
                    (sum, score, scoreIndex) => sum + (score ?? holes[scoreIndex]?.holePar ?? 0),
                    0
                );

                return Number.isFinite(computedTotal) ? computedTotal : totalPar;
            })
        );
    }, [players, holes, totalPar]);

    const getCalculatedPlayerTotal = (player) =>
        player.scores.reduce((sum, score, scoreIndex) => sum + (score ?? holes[scoreIndex]?.holePar ?? 0), 0);

    const getPlayerTotalForIndex = (playerIndex) => {
        const fallbackTotal = players[playerIndex] ? getCalculatedPlayerTotal(players[playerIndex]) : totalPar;
        return playerTotalScores[playerIndex] ?? fallbackTotal;
    };

    const setPlayerTotalScore = (playerIndex, nextValue) => {
        const parsed = parseInt(nextValue, 10);
        const fallbackTotal = getPlayerTotalForIndex(playerIndex);
        const safeValue = Number.isNaN(parsed) ? fallbackTotal : Math.max(0, parsed);

        setPlayerTotalScores((currentTotals) =>
            players.map((_, currentPlayerIndex) =>
                currentPlayerIndex === playerIndex ? safeValue : (currentTotals[currentPlayerIndex] ?? getPlayerTotalForIndex(currentPlayerIndex))
            )
        );
    };

    const adjustPlayerTotal = (playerIndex, delta) => {
        const currentValue = getPlayerTotalForIndex(playerIndex);
        setPlayerTotalScore(playerIndex, Math.max(0, currentValue + delta));
    };

    const setPlayerHoleScore = (playerIndex, holeIndex, nextValue) => {
        setPlayers((currentPlayers) =>
            currentPlayers.map((player, currentPlayerIndex) => {
                if (currentPlayerIndex !== playerIndex) {
                    return player;
                }

                const parsed = parseInt(nextValue, 10);
                const fallbackPar = holes[holeIndex]?.holePar ?? 0;
                const safeValue = Number.isNaN(parsed) ? fallbackPar : Math.max(0, parsed);
                const nextScores = [...player.scores];
                nextScores[holeIndex] = safeValue;

                return {
                    ...player,
                    scores: nextScores,
                };
            })
        );

        setPlayerTotalScores((currentTotals) =>
            players.map((player, currentPlayerIndex) => {
                if (currentPlayerIndex !== playerIndex) {
                    return currentTotals[currentPlayerIndex] ?? getPlayerTotalForIndex(currentPlayerIndex);
                }

                const parsed = parseInt(nextValue, 10);
                const fallbackPar = holes[holeIndex]?.holePar ?? 0;
                const safeValue = Number.isNaN(parsed) ? fallbackPar : Math.max(0, parsed);
                const nextScores = [...player.scores];
                nextScores[holeIndex] = safeValue;

                return nextScores.reduce((sum, score, scoreIndex) => sum + (score ?? holes[scoreIndex]?.holePar ?? 0), 0);
            })
        );
    };

    const updateScore = (holeIndex, nextValue) => {
        setPlayerHoleScore(activePlayerIndex, holeIndex, nextValue);
    };

    const adjustScore = (holeIndex, delta, playerIndex = activePlayerIndex) => {
        const currentValue = players[playerIndex]?.scores[holeIndex] ?? holes[holeIndex]?.holePar ?? 0;
        setPlayerHoleScore(playerIndex, holeIndex, Math.max(0, currentValue + delta));
    };

    const activePlayerTotal = getPlayerTotalForIndex(activePlayerIndex);
    const activePlayerRelativeMeta = getRelativeToParMeta(activePlayerTotal, totalPar);
    const isFinalPlayer = activePlayerIndex === players.length - 1;
    const isFinalHole = activeHoleIndex === holes.length - 1;
    const activePrizePlayerName =
        activePrizeContext.playerIndex !== null ? (players[activePrizeContext.playerIndex]?.name || "") : "";

    const handleScorePhotoChange = (event) => {
        const selectedFile = event.target.files?.[0];
        setScorePhotoName(selectedFile?.name || "");
    };

    const openPrizeModal = (playerIndex, holeIndex, prizeCode) => {
        const entryKey = `${playerIndex}-${holeIndex}`;
        const existingEntry = prizeEntries[entryKey] || { feet: "", inches: "" };

        setActivePrizeContext({ playerIndex, holeIndex, prizeCode });
        setPrizeFeet(existingEntry.feet);
        setPrizeInches(existingEntry.inches);
        setShowPrizeModal(true);
    };

    const handleSavePrizeEntry = () => {
        if (activePrizeContext.playerIndex === null || activePrizeContext.holeIndex === null) {
            setShowPrizeModal(false);
            return;
        }

        const entryKey = `${activePrizeContext.playerIndex}-${activePrizeContext.holeIndex}`;
        setPrizeEntries((currentEntries) => ({
            ...currentEntries,
            [entryKey]: {
                feet: prizeFeet,
                inches: prizeInches,
            },
        }));
        setShowPrizeModal(false);
    };

    return (
        <>
            <Header organization={organization} showBanner={false} />
            <Container className="mt-4 mb-5">
                <Row className="mb-3">
                    <Col>
                        <Alert color="light" className="mb-0" fade={false}>
                            {weekCourseLabel}
                        </Alert>
                    </Col>
                </Row>

                {!showSubmittedScores ? (
                    <Row className="mb-3">
                        <Col className="d-flex justify-content-center">
                            <ButtonGroup>
                                <Button
                                    color={scoreEntryMode === "player" ? "primary" : "secondary"}
                                    outline={scoreEntryMode !== "player"}
                                    onClick={() => {
                                        setScoreEntryMode("player");
                                        setActivePlayerIndex(0);
                                        setShowSubmittedScores(false);
                                    }}
                                >
                                    By Player
                                </Button>
                                <Button
                                    color={scoreEntryMode === "hole" ? "primary" : "secondary"}
                                    outline={scoreEntryMode !== "hole"}
                                    onClick={() => {
                                        setScoreEntryMode("hole");
                                        setActiveHoleIndex(0);
                                        setShowSubmittedScores(false);
                                    }}
                                >
                                    By Hole
                                </Button>
                                <Button
                                    color={scoreEntryMode === "total" ? "primary" : "secondary"}
                                    outline={scoreEntryMode !== "total"}
                                    onClick={() => {
                                        setScoreEntryMode("total");
                                        setShowSubmittedScores(false);
                                    }}
                                >
                                    By Total
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                ) : null}

                {loadingOrg ? (
                    <Row>
                        <Col className="text-center py-4">
                            <Spinner color="primary" />
                        </Col>
                    </Row>
                ) : (
                    <Row className="justify-content-center">
                        <Col xs={12} md={10} lg={8}>
                            {showSubmittedScores ? (
                                <Card>
                                    <CardHeader className="fw-semibold">{scoresConfirmed ? "Confirmation" : "Submitted Team Scores"}</CardHeader>
                                    <CardBody>
                                        {scoresConfirmed ? (
                                            <>
                                                <Alert color="success" className="mb-3" fade={false}>
                                                    Thank you! Scores were submitted successfully.
                                                </Alert>
                                                <div className="border rounded p-3">
                                                    {players.map((player, playerIndex) => {
                                                        const playerTotal = getPlayerTotalForIndex(playerIndex);
                                                        const relativeMeta = getRelativeToParMeta(playerTotal, totalPar);

                                                        return (
                                                            <Row key={`confirmation-${player.id}`} className="py-1">
                                                                <Col className="d-flex justify-content-between">
                                                                    <span className="fw-semibold">{player.name}</span>
                                                                    <span className="fw-bold">
                                                                        {playerTotal} <span className={relativeMeta.className}>({relativeMeta.text})</span>
                                                                    </span>
                                                                </Col>
                                                            </Row>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="table-responsive">
                                                    <table className="table table-sm align-middle mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>Hole</th>
                                                                {players.map((player) => (
                                                                    <th key={player.id} className="text-center">{player.name}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {holes.map((hole, holeIndex) => (
                                                                <tr key={hole.holeNumber}>
                                                                    <td className="fw-semibold">{hole.holeNumber}</td>
                                                                    {players.map((player) => {
                                                                        const scoreValue = player.scores[holeIndex] ?? hole.holePar;
                                                                        return (
                                                                            <td key={`${player.id}-${hole.holeNumber}`} className="text-center fw-semibold">
                                                                                <span
                                                                                    className="d-inline-flex justify-content-center align-items-center"
                                                                                    style={{
                                                                                        ...getScoreVisualStyle(scoreValue, hole.holePar),
                                                                                        minWidth: "2rem",
                                                                                        minHeight: "2rem",
                                                                                        padding: "0.125rem 0.25rem",
                                                                                    }}
                                                                                >
                                                                                    {scoreValue}
                                                                                </span>
                                                                            </td>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            ))}
                                                            <tr>
                                                                <td className="fw-bold">Total</td>
                                                                {players.map((player, playerIndex) => {
                                                                    const playerTotal = getPlayerTotalForIndex(playerIndex);
                                                                    const relativeMeta = getRelativeToParMeta(playerTotal, totalPar);

                                                                    return (
                                                                        <td key={`${player.id}-total`} className="text-center fw-bold">
                                                                            {playerTotal} <span className={relativeMeta.className}>({relativeMeta.text})</span>
                                                                        </td>
                                                                    );
                                                                })}
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <Row className="pt-3">
                                                    <Col className="d-flex justify-content-between align-items-start">
                                                        <Button
                                                            color="secondary"
                                                            outline
                                                            onClick={() => {
                                                                setShowSubmittedScores(false);
                                                                setScoresConfirmed(false);
                                                            }}
                                                        >
                                                            Back
                                                        </Button>
                                                        <div className="d-flex flex-column align-items-end">
                                                            <Button tag="label" color="secondary" outline className="mb-2">
                                                                Take Photo of Scores
                                                                <Input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    capture="environment"
                                                                    className="d-none"
                                                                    onChange={handleScorePhotoChange}
                                                                />
                                                            </Button>
                                                            {scorePhotoName ? (
                                                                <div className="text-muted small">Selected photo: {scorePhotoName}</div>
                                                            ) : null}
                                                            <Button
                                                                color="success"
                                                                className="mt-2"
                                                                onClick={() => setScoresConfirmed(true)}
                                                            >
                                                                Submit Scores
                                                            </Button>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                    </CardBody>
                                </Card>
                            ) : (
                                scoreEntryMode === "player" ? (
                                    <Card>
                                        <CardHeader className="text-center">
                                            <div className="fw-semibold">{activePlayer.name}</div>
                                        </CardHeader>
                                        <CardBody>
                                            {holes.map((hole, holeIndex) => (
                                                <Row key={hole.holeNumber} className="align-items-center py-2 border-bottom">
                                                    <Col xs={3} md={2}>
                                                        <div className="small fw-semibold">Hole {hole.holeNumber}</div>
                                                        {hole.holePrize ? (
                                                            <div className="small text-success">{getPrizeHeader(hole.holePrize)}</div>
                                                        ) : null}
                                                    </Col>
                                                    <Col xs={2} md={2}>
                                                        <Button
                                                            color="secondary"
                                                            outline
                                                            className="w-100 d-flex align-items-center justify-content-center p-0"
                                                            style={{ minHeight: "38px" }}
                                                            onClick={() => adjustScore(holeIndex, -1, activePlayerIndex)}
                                                        >
                                                            -
                                                        </Button>
                                                    </Col>
                                                    <Col xs={3} md={4}>
                                                        {(() => {
                                                            const displayScore = activePlayer.scores[holeIndex] ?? hole.holePar;
                                                            return (
                                                                <Input
                                                                    type="text"
                                                                    value={displayScore}
                                                                    className="text-center fw-semibold"
                                                                    style={getScoreVisualStyle(displayScore, hole.holePar)}
                                                                    onChange={(e) => updateScore(holeIndex, e.target.value)}
                                                                />
                                                            );
                                                        })()}
                                                    </Col>
                                                    <Col xs={2} md={2}>
                                                        <Button
                                                            color="secondary"
                                                            outline
                                                            className="w-100 d-flex align-items-center justify-content-center p-0"
                                                            style={{ minHeight: "38px" }}
                                                            onClick={() => adjustScore(holeIndex, 1, activePlayerIndex)}
                                                        >
                                                            +
                                                        </Button>
                                                    </Col>
                                                    <Col xs={2} md={2}>
                                                        {hole.holePrize ? (
                                                            <Button
                                                                color="success"
                                                                className="w-100 d-flex align-items-center justify-content-center p-0"
                                                                style={{ minHeight: "38px" }}
                                                                aria-label={getPrizeHeader(hole.holePrize)}
                                                                onClick={() => openPrizeModal(activePlayerIndex, holeIndex, hole.holePrize)}
                                                            >
                                                                <i className="fas fa-trophy" style={{ lineHeight: 1 }} aria-hidden="true" />
                                                                <span className="visually-hidden">{getPrizeHeader(hole.holePrize)}</span>
                                                            </Button>
                                                        ) : null}
                                                    </Col>
                                                </Row>
                                            ))}

                                            <Row className="pt-3">
                                                <Col className="text-end fw-bold">
                                                    Total: {activePlayerTotal} <span className={activePlayerRelativeMeta.className}>({activePlayerRelativeMeta.text})</span>
                                                </Col>
                                            </Row>
                                            <Row className="pt-3">
                                                <Col className="d-flex justify-content-between">
                                                    {activePlayerIndex > 0 ? (
                                                        <Button
                                                            color="secondary"
                                                            outline
                                                            onClick={() => setActivePlayerIndex((prev) => Math.max(0, prev - 1))}
                                                        >
                                                            Previous Player
                                                        </Button>
                                                    ) : <div />}
                                                    {isFinalPlayer ? (
                                                        <Button
                                                            color="primary"
                                                            onClick={() => {
                                                                setShowSubmittedScores(true);
                                                                setScoresConfirmed(false);
                                                            }}
                                                        >
                                                            Submit Scores
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            color="secondary"
                                                            outline
                                                            onClick={() => setActivePlayerIndex((prev) => Math.min(players.length - 1, prev + 1))}
                                                        >
                                                            Next Player
                                                        </Button>
                                                    )}
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                ) : scoreEntryMode === "hole" ? (
                                    <Card>
                                        <CardHeader className="text-center">
                                            <div className="small fw-semibold">Hole {activeHole.holeNumber} (Par {activeHole.holePar})</div>
                                            {activeHole.holePrize ? (
                                                <div className="small text-success">{getPrizeHeader(activeHole.holePrize)}</div>
                                            ) : null}
                                        </CardHeader>
                                        <CardBody>
                                            {players.map((player, playerIndex) => {
                                                const displayScore = player.scores[activeHoleIndex] ?? activeHole.holePar;
                                                return (
                                                    <Row key={player.id} className="align-items-center py-2 border-bottom">
                                                        <Col xs={3} md={2} className="fw-semibold"><small>{player.name}</small></Col>
                                                        <Col xs={2} md={2}>
                                                            <Button
                                                                color="secondary"
                                                                outline
                                                                className="w-100 d-flex align-items-center justify-content-center p-0"
                                                                style={{ minHeight: "38px" }}
                                                                onClick={() => adjustScore(activeHoleIndex, -1, playerIndex)}
                                                            >
                                                                -
                                                            </Button>
                                                        </Col>
                                                        <Col xs={3} md={4}>
                                                            <Input
                                                                type="text"
                                                                value={displayScore}
                                                                className="text-center fw-semibold"
                                                                style={getScoreVisualStyle(displayScore, activeHole.holePar)}
                                                                onChange={(e) => setPlayerHoleScore(playerIndex, activeHoleIndex, e.target.value)}
                                                            />
                                                        </Col>
                                                        <Col xs={2} md={2}>
                                                            <Button
                                                                color="secondary"
                                                                outline
                                                                className="w-100 d-flex align-items-center justify-content-center p-0"
                                                                style={{ minHeight: "38px" }}
                                                                onClick={() => adjustScore(activeHoleIndex, 1, playerIndex)}
                                                            >
                                                                +
                                                            </Button>
                                                        </Col>
                                                        <Col xs={2} md={2}>
                                                            {activeHole.holePrize ? (
                                                                <Button
                                                                    color="success"
                                                                    className="w-100 d-flex align-items-center justify-content-center p-0"
                                                                    style={{ minHeight: "38px" }}
                                                                    aria-label={getPrizeHeader(activeHole.holePrize)}
                                                                    onClick={() => openPrizeModal(playerIndex, activeHoleIndex, activeHole.holePrize)}
                                                                >
                                                                    <i className="fas fa-trophy" style={{ lineHeight: 1 }} aria-hidden="true" />
                                                                    <span className="visually-hidden">{getPrizeHeader(activeHole.holePrize)}</span>
                                                                </Button>
                                                            ) : null}
                                                        </Col>
                                                    </Row>
                                                );
                                            })}

                                            <Row className="pt-3">
                                                <Col className="d-flex justify-content-between">
                                                    {activeHoleIndex > 0 ? (
                                                        <Button
                                                            color="secondary"
                                                            outline
                                                            onClick={() => setActiveHoleIndex((prev) => Math.max(0, prev - 1))}
                                                        >
                                                            Previous Hole
                                                        </Button>
                                                    ) : <div />}
                                                    {isFinalHole ? (
                                                        <Button
                                                            color="primary"
                                                            onClick={() => {
                                                                setShowSubmittedScores(true);
                                                                setScoresConfirmed(false);
                                                            }}
                                                        >
                                                            Submit Scores
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            color="secondary"
                                                            outline
                                                            onClick={() => setActiveHoleIndex((prev) => Math.min(holes.length - 1, prev + 1))}
                                                        >
                                                            Next Hole
                                                        </Button>
                                                    )}
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                ) : (
                                    <Card>
                                        <CardHeader className="text-center">
                                            <div className="fw-semibold">Team Totals</div>
                                            <div className="small text-muted">Par {totalPar}</div>
                                        </CardHeader>
                                        <CardBody>
                                            {players.map((player, playerIndex) => {
                                                const playerTotal = getPlayerTotalForIndex(playerIndex);
                                                const relativeMeta = getRelativeToParMeta(playerTotal, totalPar);

                                                return (
                                                    <Row key={player.id} className="align-items-center py-2 border-bottom">
                                                        <Col xs={4} md={3} className="fw-semibold align-self-start">{player.name}</Col>
                                                        <Col xs={8} md={9}>
                                                            <Row className="g-2 align-items-center">
                                                                <Col xs={3} md={3}>
                                                                    <Button
                                                                        color="secondary"
                                                                        outline
                                                                        className="w-100 d-flex align-items-center justify-content-center p-0"
                                                                        style={{ minHeight: "38px" }}
                                                                        onClick={() => adjustPlayerTotal(playerIndex, -1)}
                                                                    >
                                                                        -
                                                                    </Button>
                                                                </Col>
                                                                <Col xs={6} md={6}>
                                                                    <Input
                                                                        type="text"
                                                                        value={playerTotal}
                                                                        className="text-center fw-semibold"
                                                                        onChange={(event) => setPlayerTotalScore(playerIndex, event.target.value)}
                                                                    />
                                                                </Col>
                                                                <Col xs={3} md={3}>
                                                                    <Button
                                                                        color="secondary"
                                                                        outline
                                                                        className="w-100 d-flex align-items-center justify-content-center p-0"
                                                                        style={{ minHeight: "38px" }}
                                                                        onClick={() => adjustPlayerTotal(playerIndex, 1)}
                                                                    >
                                                                        +
                                                                    </Button>
                                                                </Col>
                                                                <Col xs={12} className="text-center fw-semibold text-nowrap pt-1">
                                                                    <span className={relativeMeta.className}>({relativeMeta.text})</span>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                    </Row>
                                                );
                                            })}

                                            {prizeHoles.length ? (
                                                <div className="table-responsive pt-3">
                                                    <table className="table table-sm align-middle mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>Hole</th>
                                                                <th>Prize</th>
                                                                {players.map((player) => (
                                                                    <th key={`prize-header-${player.id}`} className="text-center">{player.name}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {prizeHoles.map((hole) => (
                                                                <tr key={`prize-row-${hole.holeNumber}`}>
                                                                    <td className="fw-semibold">{hole.holeNumber}</td>
                                                                    <td>{getPrizeHeader(hole.holePrize)}</td>
                                                                    {players.map((player, playerIndex) => (
                                                                        <td key={`prize-${player.id}-${hole.holeNumber}`} className="text-center">
                                                                            <Button
                                                                                color="success"
                                                                                className="d-inline-flex align-items-center justify-content-center p-0"
                                                                                style={{ width: "38px", minHeight: "38px" }}
                                                                                aria-label={`${getPrizeHeader(hole.holePrize)} ${player.name}`}
                                                                                onClick={() => openPrizeModal(playerIndex, hole.holeIndex, hole.holePrize)}
                                                                            >
                                                                                <i className="fas fa-trophy" style={{ lineHeight: 1 }} aria-hidden="true" />
                                                                            </Button>
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : null}

                                            <Row className="pt-3">
                                                <Col className="d-flex justify-content-end">
                                                    <Button
                                                        color="primary"
                                                        onClick={() => {
                                                            setShowSubmittedScores(true);
                                                            setScoresConfirmed(false);
                                                        }}
                                                    >
                                                        Submit Scores
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                )
                            )}
                        </Col>
                    </Row>
                )}
            </Container>
            <Modal isOpen={showPrizeModal} toggle={() => setShowPrizeModal(false)}>
                <ModalHeader toggle={() => setShowPrizeModal(false)}>
                    {`${getPrizeHeader(activePrizeContext.prizeCode)}${activePrizePlayerName ? ` - ${activePrizePlayerName}` : ""}`}
                </ModalHeader>
                <ModalBody>
                    <Row className="g-3">
                        <Col xs={6}>
                            <Input
                                type="number"
                                min="0"
                                placeholder="Feet"
                                value={prizeFeet}
                                onChange={(event) => setPrizeFeet(event.target.value)}
                            />
                        </Col>
                        <Col xs={6}>
                            <Input
                                type="number"
                                min="0"
                                placeholder="Inches"
                                value={prizeInches}
                                onChange={(event) => setPrizeInches(event.target.value)}
                            />
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={() => setShowPrizeModal(false)}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleSavePrizeEntry}>
                        Save
                    </Button>
                </ModalFooter>
            </Modal>
            <Footer />
        </>
    );
};

export default LeagueTeamScore;
